import { ArrowSmRightIcon } from '@heroicons/react/solid'
import { useNavigate } from 'react-router-dom'
import {
  Anchor,
  Form,
  Paragraph,
  StickyFooter,
  TextareaField,
} from 'src/components'
import EmailHelpBox from 'src/components/Layout/EmailHelpBox'
import CustomButton from 'src/components/Shared/UIComponents/Button'
import InputField from 'src/components/Shared/UIComponents/InputField'
import { getBackendByRegion } from 'src/regions'
import { savedLoginUrl, savedPrincipalArn, savedRoleArn } from '../../storage'
import { useEditOrganization } from 'src/routes/Onboarding/queries'
import { useResyncSubscriptionProvisioning } from '../../queries'
import { savedTokens } from 'src/auth'
import { toast } from 'sonner'
import { permissionPolicy } from '../utils'
import TooltipLabel from 'src/components/Shared/UIComponents/TooltipLabel'

import OktaSignOn from 'src/images/OktaSignOn.png'
type Props = {
  organization: string
  username: string
  connectUrl: string
  instanceId: string
  accountId: string
  region: string
  arn: string
}

export const OktaContent = (props: Props) => {
  const navigate = useNavigate()
  const editOrganization = useEditOrganization()
  const resyncSubscription = useResyncSubscriptionProvisioning()

  const [loginUrl, setLoginUrl] = savedLoginUrl.useState('')
  const [principalArn, setPrincipalArn] = savedPrincipalArn.useState('')
  const [roleArn, setRoleArn] = savedRoleArn.useState('')

  const link = new URL(
    `/token/sso?org=${props.organization}`,
    getBackendByRegion(savedTokens.get()?.region),
  ).href

  return (
    <div className="flex flex-col gap-6 mt-8 text-secondary">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Paragraph className="font-bold underline">Step 1</Paragraph>
          <Paragraph>
            To connect with Okta as an identity management provider, follow the
            exact same steps as in this{' '}
            <Anchor
              href="https://catalog.us-east-1.prod.workshops.aws/workshops/33e6d0e7-f927-4531-abb1-f28a86ba0872/en-US/1-sso-amazonconnect-okta"
              target="_blank"
              className="underline"
            >
              installation guide
            </Anchor>{' '}
            with the following exception:
          </Paragraph>
        </div>
        <Paragraph>
          <span className="text-red-500">(!) </span> in step “1.3 Okta
          configuration - step 18) Adavanced sign-on settings”,{' '}
          <b>enter the URL</b> below as <b>ACS URL</b> in the advanced sign-on
          settings.
        </Paragraph>

        <div className="flex items-end col-span-full gap-4 mt-2">
          <InputField
            label="ACS URL"
            placeholder=""
            value={link}
            disabled
            setValue={() => {}}
            tooltip={
              <img width={500} src={OktaSignOn} alt="Okta Sign On Screenshot" />
            }
            className="bg-foreground text-secondary"
          />
          <CustomButton
            type="button"
            label="copy"
            onClick={() => {
              navigator.clipboard.writeText(link)
              toast.success('Setup link has been copied to your clipboard.')
            }}
          />
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Paragraph className="font-bold underline">Step 2</Paragraph>
          <Paragraph>
            Return the following values from your SSO setup once you have
            completed all steps from the installation guide.
          </Paragraph>
        </div>

        <Form
          className="gap-12"
          onSubmit={async (e) => {
            e.preventDefault()
            if (
              loginUrl &&
              principalArn &&
              props.region &&
              roleArn &&
              props.connectUrl &&
              props.instanceId &&
              props.accountId &&
              props.arn
            ) {
              await editOrganization.mutateAsync({
                cognito_url: '',
                userPoolAppClientId: '',
                userPoolClientSecret: '',
                userPoolId: '',
                userPoolRegion: '',
                userPoolTokenType: '',
                aws_connect_saml_url: props.connectUrl,
                iam_organization_admin_accessKey: '',
                iam_organization_admin_region: '', //didn't exist
                iam_organization_usergroup: '', //didn't exist
                redirect_uri: '', //didn't exist
                aws_connect_type: 'SSO',
                iam_organization_admin_accessSecret: '',
                iam_organization_policy_arn: '',
                code: props.organization,
                organization_connect_account_id: props.accountId,
                organization_connect_instance_id: props.instanceId,
                organization_connect_arn: props.arn,
                setupStep: 'finished',
                username: props.username,
                organization: props.organization,
                region: props.region,
                sso: {
                  loginUrl: loginUrl,
                  principalArn: principalArn,
                  region: props.region,
                  roleArn: roleArn,
                  type: 'okta',
                },
                application: {
                  chatregion: props.region,
                },
              })

              await resyncSubscription.mutateAsync({
                organization: props.organization,
              })

              navigate('../finish')
            } else {
              toast.error(`ARN is not valid.`)
            }
          }}
        >
          <div className="col-span-full grid gap-8">
            <div className="flex flex-col gap-4">
              <Paragraph>a) Please enter the Login URL below</Paragraph>
              <InputField
                label="Okta Login Url"
                placeholder="https://<your-okta-domain>.okta.com/home/app_name/<application_id>/sso/saml"
                value={loginUrl}
                setValue={(value) => {
                  setLoginUrl(value.trim())
                }}
                type="url"
                className="col-span-full"
                tooltip={
                  <p>
                    Navigate to <b>Okta admin console -&gt; Applications</b> and
                    look for <b>"App Embed link"</b> or <b>"Login URL"</b>.
                    Please paste this url below.
                  </p>
                }
              />
            </div>

            <div className="flex flex-col gap-8">
              <div className="flex flex-col gap-4">
                <Paragraph>
                  b) Please enter the ARN of the these resources below
                </Paragraph>
                <div className="flex flex-col gap-4">
                  <InputField
                    label="Identity provider ARN"
                    placeholder="arn:aws:iam::ACCOUNT_ID:saml-provider/PROVIDER_ID"
                    value={principalArn}
                    setValue={(value) => {
                      setPrincipalArn(value.trim())
                    }}
                    title="arn:aws:iam::ACCOUNT_ID:saml-provider/PROVIDER_ID"
                    pattern="arn:aws:iam::\d{12}:saml-provider\/[A-Za-z0-9\-_]+"
                    className="col-span-full"
                    tooltip={
                      <p>
                        Navigate to{' '}
                        <b>
                          AWS Management console -&gt; IAM -&gt; Identity
                          Providers -&gt; Okta_Connect_Admin -&gt; ARN
                        </b>
                      </p>
                    }
                  />
                  <InputField
                    label="Role ARN"
                    placeholder="arn:aws:iam::ACCOUNT_ID:role/ROLE_ID"
                    value={roleArn}
                    setValue={(value) => {
                      setRoleArn(value.trim())
                    }}
                    title="arn:aws:iam::ACCOUNT_ID:role/ROLE_ID"
                    pattern="arn:aws:iam::\d{12}:role\/[A-Za-z0-9\-_]+"
                    className="col-span-full"
                    tooltip={
                      <p>
                        Navigate to{' '}
                        <b>
                          AWS Management console -&gt; IAM -&gt; Roles -&gt;
                          Click Okta role -&gt; ARN
                        </b>
                      </p>
                    }
                  />
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <TooltipLabel
                  label={
                    <Paragraph className="font-normal text-[16px]">
                      c) Please change the condition in your permission policy
                      to the below:
                    </Paragraph>
                  }
                  tooltip={
                    <div className="flex flex-col gap-4">
                      <Paragraph className="font-bold">
                        AWS Management Console -&gt; IAM &gt; Roles &gt;
                        ROLE_NAME
                      </Paragraph>
                      <img
                        alt="Permission policy"
                        width={600}
                        src={require('src/images/permission_policy.png')}
                      />
                    </div>
                  }
                />
                <div className="flex flex-row gap-4 items-center">
                  <TextareaField
                    classNameInput="h-32 text-sm overflow-hidden"
                    className="w-1/2"
                    value={permissionPolicy}
                    disabled
                  />
                  <CustomButton
                    type="button"
                    label="copy"
                    onClick={() => {
                      navigator.clipboard.writeText(permissionPolicy)
                      toast.success(
                        `Permission policy has been copied to your clipboard.`,
                      )
                    }}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Paragraph className="font-bold underline">Step 3</Paragraph>
                <Paragraph>
                  As a last step, please make sure that the you assign the newly
                  created role in Okta to the users that you would like to give
                  access to Mobile Connect.
                </Paragraph>
              </div>
            </div>
          </div>
          <StickyFooter>
            <EmailHelpBox className="flex-1" />
            <CustomButton
              type="submit"
              label="next"
              isLoading={
                editOrganization.isLoading || resyncSubscription.isLoading
              }
              icon={<ArrowSmRightIcon />}
              iconPosition="right"
            />
          </StickyFooter>
        </Form>
      </div>
    </div>
  )
}
